import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, MessageSquare, Pencil, Plus, Trash2 } from 'lucide-react'
import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { AdminGestionDialog } from '../components/AdminGestionDialog'
import { ConsumoMetaFiltersBar } from '../components/ConsumoMetaFiltersBar'
import {
  useConsumoMeta,
  useCreateConsumoMeta,
  useDeleteConsumoMeta,
  useUpdateConsumoMeta,
} from '../hooks/useConsumoMeta'
import { PAISES_META } from '../types/consumoMeta'
import type { ConsumoMeta, ConsumoMetaFilters, TipoEnvio } from '../types/consumoMeta'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(n)

export function ConsumoMetaLabPage() {
  const { labId } = useParams<{ labId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || user?.groups?.includes('admin') || false

  const anioParam = Number(searchParams.get('anio'))
  const mesParam = Number(searchParams.get('mes'))

  const [filters, setFilters] = useState<ConsumoMetaFilters>({
    laboratorio_id: Number(labId),
    anio: anioParam || new Date().getFullYear(),
    mes: mesParam || new Date().getMonth() + 1,
  })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<ConsumoMeta | null>(null)

  const { data: registros = [], isLoading } = useConsumoMeta(filters)

  const filtersBase = { laboratorio_id: Number(labId), anio: filters.anio, mes: filters.mes }
  const { data: registrosBase = [] } = useConsumoMeta(filtersBase)
  const paisesDisponibles = [...new Set(registrosBase.map((r) => r.pais))]

  const labNombre = registros[0]?.laboratorio_nombre ?? registrosBase[0]?.laboratorio_nombre ?? '—'

  const createMutation = useCreateConsumoMeta()
  const updateMutation = useUpdateConsumoMeta()
  const deleteMutation = useDeleteConsumoMeta()

  const handleOpenEdit = (record: ConsumoMeta) => {
    setSelectedRecord(record)
    setDialogOpen(true)
  }

  const handleOpenCreate = () => {
    setSelectedRecord(null)
    setDialogOpen(true)
  }

  const handleDelete = (record: ConsumoMeta) => {
    if (confirm(`¿Eliminar el registro "${record.proyecto} / ${record.envio}"?`)) {
      deleteMutation.mutate(record.id)
    }
  }

  const handleSubmit = async (data: any, id?: number) => {
    if (id) {
      await updateMutation.mutateAsync({ id, data })
    } else {
      await createMutation.mutateAsync({ ...data, laboratorio_id: Number(labId) })
    }
    setDialogOpen(false)
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const totalEnvios = registros.reduce((acc, r) => acc + r.total_envio, 0)
  const totalDolares = registros.reduce((acc, r) => acc + r.total_valor, 0)

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate('/consumo-meta')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="rounded-lg bg-emerald-500/10 p-2">
            <MessageSquare className="h-6 w-6 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-black dark:text-white">{labNombre}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Detalle de envíos</p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={handleOpenCreate} className="bg-emerald-800 hover:bg-emerald-900 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo registro
          </Button>
        )}
      </div>

      {/* Stats */}
      {registros.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Total envíos', value: totalEnvios.toLocaleString('es-EC') },
            { label: 'Total $', value: fmt(totalDolares) },
          ].map(({ label, value }) => (
            <Card key={label} className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 via-50% to-transparent dark:from-emerald-800/20 dark:via-emerald-900/10 dark:via-50% dark:to-transparent">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                <p className="text-xl font-bold text-black dark:text-white">{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <ConsumoMetaFiltersBar
          filters={filters}
          onFiltersChange={(f) => setFilters({ ...f, laboratorio_id: Number(labId) })}
          paisesDisponibles={paisesDisponibles}
        />
        <Select
          value={filters.tipo_envio ?? 'todos'}
          onValueChange={(v) =>
            setFilters((prev) => ({ ...prev, tipo_envio: v === 'todos' ? undefined : (v as TipoEnvio) }))
          }
        >
          <SelectTrigger className="w-40 h-8 text-sm">
            <SelectValue placeholder="Tipo de envío" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            <SelectItem value="utilidad">Utilidad</SelectItem>
            <SelectItem value="marketing">Marketing</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabla de detalle */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !registros.length ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              No hay registros para los filtros seleccionados.
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
                <TableRow>
                  <TableHead>Proyecto</TableHead>
                  <TableHead>Envío</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Total envío</TableHead>
                  <TableHead className="text-right">Valor unit.</TableHead>
                  <TableHead className="text-right">Total $</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>País</TableHead>
                  {isAdmin && <TableHead className="w-20" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {registros.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.proyecto}</TableCell>
                    <TableCell>{r.envio}</TableCell>
                    <TableCell className="max-w-[180px] truncate text-gray-500 dark:text-gray-400 text-sm">
                      {r.descripcion || '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.total_envio.toLocaleString('es-EC')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(r.valor)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-emerald-700 dark:text-emerald-400">
                      {fmt(r.total_valor)}
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                        r.tipo_envio === 'marketing'
                          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                          : 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
                      )}>
                        {r.tipo_envio === 'marketing' ? 'Marketing' : 'Utilidad'}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-500 dark:text-gray-400 text-sm">
                      {PAISES_META.find((p) => p.value === r.pais)?.label ?? r.pais}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenEdit(r)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => handleDelete(r)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {isAdmin && (
        <AdminGestionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          record={selectedRecord}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  )
}
